import React from 'react';
import './GroupSignupTab.css';
import SegmentedControl from '../../input/SegmentedControl';
import CustomTogglebox from '../../input/CustomTogglebox';
import { SELF_SERVICE_BOOKING_LABEL, SIGNUP_CHANNEL_LABEL } from '../../../util/participantGroups';
import { mailSlotLabel } from '../../../util/mailSlots';
import { INTRO_MAX } from './groupAdminUtils';

const CHANNEL_OPTIONS = [
  { value: 'Form', label: SIGNUP_CHANNEL_LABEL.Form, icon: 'description', tone: 'accent' },
  { value: 'PersonalLink', label: SIGNUP_CHANNEL_LABEL.PersonalLink, icon: 'link', tone: 'accent' },
];

const BOOKING_OPTIONS = [
  { value: 'Inbox', label: SELF_SERVICE_BOOKING_LABEL.Inbox, icon: 'inbox', tone: 'accent' },
  { value: 'Immediate', label: SELF_SERVICE_BOOKING_LABEL.Immediate, icon: 'bolt', tone: 'accent' },
];

const CHANNEL_EXPLANATION = {
  Form: 'Personen dieser Gruppe melden sich wie Gäste über das Formular an. Beim Annehmen in der Inbox zählt ihre Gruppe am Spieltag (Preis und Mails).',
  PersonalLink: 'Personen dieser Gruppe bekommen einen persönlichen Link (Seite auf der Homepage) und melden sich dort für die für sie freigegebenen Spieltage an oder ab.',
};

const BOOKING_EXPLANATION = {
  Inbox: 'Anmeldungen landen in der Inbox und werden von der Orga angenommen.',
  Immediate: 'Anmeldungen werden sofort gebucht – ist der Spieltag voll, landet die Person auf der Warteliste.',
};

// Shown by the personal-link page when the group has no intro — keep in sync with DEFAULT_INTRO in
// wordpress-plugin/ave-custom-orga-plugin/js/member-event-response.js.
const DEFAULT_INTRO = 'Hier sind die anstehenden Spieltage. Meldet euch direkt an oder gebt eine Absage.';

// Status mails a person needs after an immediate booking (otherwise no confirmation arrives).
const IMMEDIATE_SLOTS = ['confirmed', 'waitlist'];

/** Tab "Anmeldung": sign-up channel; for personal links booking mode, add-ons and the page intro. */
function GroupSignupTab({ form, setField, errors, group, isCreate, baseline, mailRules, onOpenMails }) {
  if (group?.isGuest) {
    return (
      <div className="groupTabBody">
        <section className="groupEditorSection">
          <h4 className="groupEditorSectionTitle">Anmeldeweg</h4>
          <div className="groupSignupFixed">
            <span className="material-symbols-rounded groupSignupFixedIcon" aria-hidden="true">description</span>
            <div className="groupSignupFixedText">
              <span className="groupSignupFixedTitle">Formular – öffentliche Anmeldung</span>
              <span className="groupSignupFixedBody">
                Gäste melden sich über das öffentliche Anmeldeformular bzw. den Einladungslink privater Events an.
                Die Anmeldung landet in der Inbox; beim Annehmen zählt die Gruppe der Person am Spieltag
                (unbekannte Personen sind Gäste).
              </span>
            </div>
          </div>
          <p className="formFieldHint">Für die Systemgruppe fest – nicht änderbar.</p>
        </section>
      </div>
    );
  }

  const isPersonalLink = form.signupChannel === 'PersonalLink';
  const isImmediate = isPersonalLink && form.selfServiceBooking === 'Immediate';

  // Warning only once the (unsaved) mail rules of this group are known. A rule without textblock sends
  // nothing, just like a switched-off one.
  const disabledImmediateSlots = isImmediate && mailRules
    ? IMMEDIATE_SLOTS.filter(slot => mailRules.some(r => r.slot === slot && (!r.enabled || r.textblockId == null)))
    : [];

  const losesPersonalLink = !isCreate && group
    && baseline.signupChannel === 'PersonalLink' && form.signupChannel === 'Form'
    && Number(group.currentMemberCount ?? 0) > 0;

  const intro = form.selfServiceIntro;

  return (
    <div className="groupTabBody">
      <section className="groupEditorSection">
        <h4 className="groupEditorSectionTitle">Anmeldeweg</h4>
        <SegmentedControl
          options={CHANNEL_OPTIONS}
          value={form.signupChannel}
          onChange={value => setField('signupChannel', value)}
          ariaLabel="Anmeldeweg"
        />
        <p className="groupEditorExplanation">{CHANNEL_EXPLANATION[form.signupChannel]}</p>
        {losesPersonalLink && (
          <p className="groupEditorWarning">
            Die {group.currentMemberCount === 1 ? 'Person' : `${group.currentMemberCount} Personen`} dieser Gruppe
            {group.currentMemberCount === 1 ? ' verliert' : ' verlieren'} damit den Zugang zu ihrer persönlichen Seite –
            der Link funktioniert nur für Gruppen mit persönlichem Link.
          </p>
        )}
      </section>

      {isPersonalLink && (
        <React.Fragment>
          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Buchung über den persönlichen Link</h4>
            <SegmentedControl
              options={BOOKING_OPTIONS}
              value={form.selfServiceBooking}
              onChange={value => setField('selfServiceBooking', value)}
              ariaLabel="Buchung über den persönlichen Link"
            />
            <p className="groupEditorExplanation">{BOOKING_EXPLANATION[form.selfServiceBooking]}</p>
            {disabledImmediateSlots.length > 0 && (
              <div className="groupEditorWarning groupSignupMailWarning">
                <span>
                  Bei „Sofort gebucht“ erfährt die Person per Mail, ob sie gebucht ist oder auf der Warteliste steht.
                  Für diese Gruppe {disabledImmediateSlots.length === 1 ? 'ist die Mail' : 'sind die Mails'}{' '}
                  {disabledImmediateSlots.map(s => `„${mailSlotLabel(s)}“`).join(' und ')} ausgeschaltet oder ohne Textbaustein.
                </span>
                <button type="button" className="groupSignupLinkBtn" onClick={onOpenMails}>
                  Mail-Regeln öffnen
                </button>
              </div>
            )}
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Zusätze</h4>
            <CustomTogglebox
              placeholder="Zusätze über den persönlichen Link buchbar"
              checked={form.selfServiceAddOns}
              onChange={e => setField('selfServiceAddOns', e.target.checked)}
            />
            <p className="formFieldHint">
              Zeigt auf der persönlichen Seite je Spieltag die Zusätze seines Prozesses, die über die Homepage buchbar
              sind (z. B. Leihausrüstung) – mit dem Hinweis kostenlos bzw. kostenpflichtig, ohne Preis. Spätere
              Änderungen an gebuchten Zusätzen übernimmt die Orga.
            </p>
          </section>

          <section className="groupEditorSection">
            <h4 className="groupEditorSectionTitle">Hinweistext der persönlichen Seite</h4>
            <div className="formFieldWrapper">
              <label className="formFieldLabel" htmlFor="groupSelfServiceIntro">Hinweistext</label>
              <textarea
                id="groupSelfServiceIntro"
                className={`groupEditorTextarea isTall${errors.selfServiceIntro ? ' isInvalid' : ''}`}
                rows={5}
                maxLength={INTRO_MAX}
                value={intro}
                onChange={e => setField('selfServiceIntro', e.target.value)}
                placeholder="Leer lassen für den neutralen Standardtext"
              />
            </div>
            <div className="groupSignupIntroMeta">
              <p className="formFieldHint">
                Reiner Text (ohne Formatierung), steht oben auf der persönlichen Seite. Leer = neutraler Standardtext.
                Den Hinweis auf private Events ergänzt die Seite selbst.
              </p>
              <span className="groupSignupIntroCounter">{intro.length} / {INTRO_MAX}</span>
            </div>
            {errors.selfServiceIntro && <p className="groupEditorFieldError">{errors.selfServiceIntro}</p>}

            <div className="groupSignupPreview">
              <span className="groupSignupPreviewLabel">Vorschau</span>
              <div className="groupSignupPreviewPage">
                <p className="groupSignupPreviewGreeting">Hallo Max,</p>
                {intro.trim()
                  ? <p className="groupSignupPreviewText">{intro}</p>
                  : <p className="groupSignupPreviewText isDefault">{DEFAULT_INTRO}</p>}
              </div>
            </div>
          </section>
        </React.Fragment>
      )}
    </div>
  );
}

export default GroupSignupTab;
